import { Box, Grid } from '@material-ui/core';
import { LenderName } from '@nemuru-eng/js-api-client';
import { useFormContext } from 'react-hook-form';

import { getLendersFromPricing } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/utils/getLendersFromPricing';
import { getAllowedLendersByPaymentMethodType } from '@/components/modules/PricingsList/_shared/constants/getAllowedLendersByPaymentMethodType';
import {
  IBERIA_CARDS_VISIBLE_LENDERS,
  LENDERS_TO_HIDE,
} from '@/components/modules/PricingsList/components/Filters/components/LendersList/constants/constants';
import { useMerchantAcquisitionChannel } from '@/components/modules/PricingsList/components/Filters/components/LendersList/hooks/useMerchantAcquisitionChannel';
import { shouldApplyIberiaLenderWhitelist } from '@/components/modules/PricingsList/components/Filters/components/LendersList/maps/shouldApplyIberiaLenderWhitelist.map';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { Font } from '@/components/shared/components/Font';
import { LoadingValues } from '@/components/shared/components/LoadingValues/LoadingValues';
import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { StyledTooltip } from '@/components/shared/components/StyledTooltip';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useOperationsContext } from '@/contexts/OperationsContext/OperationsContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface LendersListProps {
  selectedLender: LenderName | undefined;
  setSelectedLender: React.Dispatch<React.SetStateAction<LenderName | undefined>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}
export const LendersList = ({ selectedLender, setSelectedLender, setPage }: LendersListProps) => {
  const { watch } = useFormContext();
  const { getTextIdTranslation } = useTranslation();
  const {
    state: { pricingsListEntity },
  } = useOperationsContext();

  const { palette } = useCustomTheme();
  const { acquisitionChannel, merchantId, merchantLoading } = useMerchantAcquisitionChannel();

  const availablePricingsByPaymentMethodType = pricingsListEntity?.getPricingsByPaymentMethodFilter();

  const lenderExistsInPricings = (lenderName: LenderName) => {
    return availablePricingsByPaymentMethodType?.some((pricing) => {
      const lenderNames = getLendersFromPricing(pricing);
      return lenderNames.includes(lenderName);
    });
  };

  const selectedPaymentMethod = watch('paymentMethod');
  const lenders = getAllowedLendersByPaymentMethodType(selectedPaymentMethod);
  const filteredLenders = lenders
    .filter((lender) => !LENDERS_TO_HIDE.includes(lender.name))
    .filter((lender) => {
      if (!merchantId || !acquisitionChannel) return true;
      if (!shouldApplyIberiaLenderWhitelist(merchantId, acquisitionChannel)) return true;
      return IBERIA_CARDS_VISIBLE_LENDERS.includes(lender.name);
    });

  const sortedLenders = filteredLenders.sort((a, b) => {
    const aExists = lenderExistsInPricings(a.name);
    const bExists = lenderExistsInPricings(b.name);

    if (aExists && !bExists) return -1;
    if (!aExists && bExists) return 1;
    return 0;
  });

  if (sortedLenders.length === 0) return null;

  return (
    <Box display={'flex'} flexDirection={'column'} gridGap={16}>
      <Font variant="subtitle2" color="title">
        <Text strong tId={'LENDERS'} />
      </Font>
      <Grid container spacing={1}>
        {merchantLoading ? (
          <LoadingValues />
        ) : (
          sortedLenders.map((lender) => {
            const isLenderSelected = selectedLender === lender.name;
            return (
              <Grid item>
                <CustomPaper
                  fill="paper"
                  style={{
                    height: 12,
                    width: 12,
                    padding: 16,
                    boxShadow: isLenderSelected ? `0 0 0 2px ${palette.primary.main}` : '',
                    cursor: 'pointer',
                  }}
                >
                  <Box
                    height={'100%'}
                    width={'100%'}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    boxSizing={'border-box'}
                    onClick={
                      lenderExistsInPricings(lender.name)
                        ? () => {
                            setPage(0);
                            setSelectedLender((s) => (s === lender.name ? undefined : lender.name));
                          }
                        : undefined
                    }
                  >
                    <StyledTooltip
                      tooltipProps={{ enterDelay: 100 }}
                      title={
                        !lenderExistsInPricings(lender.name) ? (
                          <Text
                            tId={'LENDER_NOT_AVAILABLE_WARNING'}
                            dynamicDataTexts={[getTextIdTranslation(lender.name)]}
                          />
                        ) : (
                          ''
                        )
                      }
                    >
                      <LenderIcon
                        isActive={lenderExistsInPricings(lender.name) ? true : false}
                        size={25}
                        lenderName={lender.name}
                      />
                    </StyledTooltip>
                  </Box>
                </CustomPaper>
              </Grid>
            );
          })
        )}
      </Grid>
    </Box>
  );
};
